import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import {
  ArrowRight,
  Calendar,
  Clock,
  MessageSquare,
  Phone,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Target,
  TrendingDown,
  TrendingUp,
  Voicemail,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { DailySeriesByMetric, TeamTotals } from '@/types/teamPerformance'
import type { DrilldownMetric } from '@/types/analyticsDrilldown'
import { Sparkline } from './Sparkline'

interface Props {
  current: TeamTotals
  previous: TeamTotals
  dailySeries: DailySeriesByMetric
  period: string
  darkMode: boolean
}

// Mapping carte → metric drilldown. answer_rate_pct est volontairement absent
// (c'est un ratio dérivé, pas drillable).
const CARD_TO_METRIC: Partial<Record<keyof TeamTotals, DrilldownMetric>> = {
  calls_total: 'calls_total',
  calls_inbound: 'calls_inbound',
  calls_outbound: 'calls_outbound',
  missed_total: 'missed',
  talk_time_seconds: 'talk_time',
  sms_sent: 'sms_sent',
  appointments_created: 'appointments_created',
  voicemails_listened: 'voicemails_listened',
}

// Couleur de sparkline par métrique. SVG nécessite des couleurs CSS littérales.
const SPARKLINE_COLOR_LIGHT: Partial<Record<keyof TeamTotals, string>> = {
  calls_total: '#6366f1',
  calls_inbound: '#10b981',
  calls_outbound: '#6366f1',
  missed_total: '#f43f5e',
}
const SPARKLINE_COLOR_DARK: Partial<Record<keyof TeamTotals, string>> = {
  calls_total: '#818cf8',
  calls_inbound: '#34d399',
  calls_outbound: '#818cf8',
  missed_total: '#fb7185',
}

function formatDuration(seconds: number): string {
  if (!seconds) return '0 min'
  const totalMinutes = Math.round(seconds / 60)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`
  return `${minutes} min`
}

interface CardSpec {
  key: keyof TeamTotals
  icon: typeof Phone
  format: (v: number) => string
  // Pour answer_rate_pct, on inverse la sémantique : ↑ = bon
  reverseDelta?: boolean
  // Pour missed : ↑ = mauvais (en rouge même si chiffre augmente)
  negativeWhenUp?: boolean
}

// Cartes hero : les 4 KPI fondamentaux du téléphonie manager.
// Sparkline visible avec pic annoté — c'est notre signature visuelle.
const HERO_CARDS: CardSpec[] = [
  { key: 'calls_total', icon: Phone, format: (v) => v.toLocaleString() },
  { key: 'calls_inbound', icon: PhoneIncoming, format: (v) => v.toLocaleString() },
  { key: 'calls_outbound', icon: PhoneOutgoing, format: (v) => v.toLocaleString() },
  {
    key: 'missed_total',
    icon: PhoneMissed,
    format: (v) => v.toLocaleString(),
    negativeWhenUp: true,
  },
]

// Cartes secondaires : compactes, chiffre + delta uniquement, pas de sparkline.
// La sparkline serait du bruit ici (ratio dérivé, volumes faibles, etc.).
const SECONDARY_CARDS: CardSpec[] = [
  { key: 'answer_rate_pct', icon: Target, format: (v) => `${v}%` },
  { key: 'talk_time_seconds', icon: Clock, format: formatDuration },
  { key: 'sms_sent', icon: MessageSquare, format: (v) => v.toLocaleString() },
  { key: 'appointments_created', icon: Calendar, format: (v) => v.toLocaleString() },
  { key: 'voicemails_listened', icon: Voicemail, format: (v) => v.toLocaleString() },
]

function computeDelta(curr: number, prev: number): { pct: number | null; abs: number } {
  const abs = curr - prev
  if (prev === 0) return { pct: curr === 0 ? 0 : null, abs }
  return { pct: Math.round((abs / prev) * 100), abs }
}

// Seuil sous lequel la période précédente est considérée comme "quasi vide" :
// les % de comparaison deviennent absurdes (+11500%…) — on bascule sur "Nouveau".
const PREV_NEGLIGIBLE_THRESHOLD = 3

export function TeamTotalsCards({ current, previous, dailySeries, period, darkMode }: Props) {
  return (
    <div className="space-y-3">
      {/* Top row : 4 cartes hero avec sparkline */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {HERO_CARDS.map((spec) => (
          <KpiCard
            key={spec.key}
            spec={spec}
            variant="hero"
            current={current}
            previous={previous}
            dailySeries={dailySeries}
            period={period}
            darkMode={darkMode}
          />
        ))}
      </div>
      {/* Bottom row : 5 cartes secondaires compactes */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {SECONDARY_CARDS.map((spec) => (
          <KpiCard
            key={spec.key}
            spec={spec}
            variant="compact"
            current={current}
            previous={previous}
            dailySeries={dailySeries}
            period={period}
            darkMode={darkMode}
          />
        ))}
      </div>
    </div>
  )
}

interface KpiCardProps {
  spec: CardSpec
  variant: 'hero' | 'compact'
  current: TeamTotals
  previous: TeamTotals
  dailySeries: DailySeriesByMetric
  period: string
  darkMode: boolean
}

function KpiCard({
  spec,
  variant,
  current,
  previous,
  dailySeries,
  period,
  darkMode,
}: KpiCardProps) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const { key, icon: Icon, format, negativeWhenUp } = spec
  const curr = current[key] as number
  const prev = previous[key] as number
  const { pct, abs } = computeDelta(curr, prev)
  const isUp = abs > 0
  const isDown = abs < 0
  const goodUp = !negativeWhenUp
  const positiveTone = (isUp && goodUp) || (isDown && !goodUp)
  const isNew = prev < PREV_NEGLIGIBLE_THRESHOLD && curr > 0
  const showArrow = abs !== 0 && pct !== null && !isNew

  const metric = CARD_TO_METRIC[key]
  const drilldownUrl = metric
    ? `/${locale}/manager/analytics/drilldown?metric=${metric}&period=${period}`
    : null

  const series = dailySeries[key]
  const sparkColor = darkMode ? SPARKLINE_COLOR_DARK[key] : SPARKLINE_COLOR_LIGHT[key]
  const showSparkline = variant === 'hero' && !!series && series.length > 1 && !!sparkColor

  const cardBg = darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
  const cardHover = darkMode
    ? 'hover:border-indigo-500/60 hover:bg-slate-800/80'
    : 'hover:border-indigo-300 hover:shadow-sm'
  const labelColor = darkMode ? 'text-slate-400' : 'text-slate-500'
  const valueColor = darkMode ? 'text-white' : 'text-slate-900'
  const iconBg = darkMode ? 'bg-slate-700/60' : 'bg-slate-100'
  const iconColor = darkMode ? 'text-slate-300' : 'text-slate-600'
  const linkColor = darkMode
    ? 'text-indigo-300 hover:text-indigo-200'
    : 'text-indigo-600 hover:text-indigo-700'

  const padding = variant === 'hero' ? 'p-5' : 'p-4'
  const valueSize = variant === 'hero' ? 'text-3xl' : 'text-xl'
  const iconSize = variant === 'hero' ? 'h-9 w-9' : 'h-8 w-8'
  const iconInner = variant === 'hero' ? 'h-5 w-5' : 'h-4 w-4'

  const inner = (
    <>
      <div className="flex items-center justify-between">
        <div
          className={cn(
            'rounded-lg flex items-center justify-center flex-shrink-0',
            iconSize,
            iconBg,
          )}
        >
          <Icon className={cn(iconInner, iconColor)} />
        </div>
        {showArrow && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 text-xs font-medium',
              positiveTone
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-rose-600 dark:text-rose-400',
            )}
            title={t('manager.analytics.deltaTooltip', {
              diff: abs > 0 ? `+${abs}` : abs,
            })}
          >
            {isUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {pct !== null && `${pct > 0 ? '+' : ''}${pct}%`}
          </span>
        )}
        {isNew && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 text-xs font-medium',
              goodUp
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-amber-600 dark:text-amber-400',
            )}
            title={t('manager.analytics.deltaNewTooltip', { prev, curr })}
          >
            <TrendingUp className="h-3 w-3" />
            {t('manager.analytics.deltaNew')}
          </span>
        )}
      </div>

      <div className={cn('font-semibold tabular-nums leading-none', valueSize, valueColor)}>
        {format(curr)}
      </div>
      <div className={cn('text-xs', labelColor)}>
        {t(`manager.analytics.cards.${key}`)}
      </div>

      {showSparkline && (
        <Sparkline
          data={series!}
          color={sparkColor!}
          height={48}
          locale={locale}
          textColor={cn('text-[10px]', labelColor)}
          className="mt-1 -mx-1"
        />
      )}

      {drilldownUrl && (
        <div className="mt-auto pt-2">
          <span className={cn('inline-flex items-center gap-1 text-xs font-medium', linkColor)}>
            {t('manager.analytics.viewDetails')}
            <ArrowRight className="h-3 w-3" />
          </span>
        </div>
      )}
    </>
  )

  const baseClasses = cn(
    'rounded-xl border flex flex-col gap-2 transition-all',
    padding,
    cardBg,
    drilldownUrl && cardHover,
  )

  return drilldownUrl ? (
    <Link
      to={drilldownUrl}
      className={cn(
        baseClasses,
        'cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500/40',
      )}
      aria-label={t('manager.analytics.drilldown.openCard', {
        label: t(`manager.analytics.cards.${key}`),
      })}
    >
      {inner}
    </Link>
  ) : (
    <div className={baseClasses}>{inner}</div>
  )
}
